import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNotificationPanel, useNotificationsContext } from '../../context/NotificationsContext'
import { useNotificationToasts } from '@/hooks/useNotificationToasts'
import { useNotificationPreferences } from '@/hooks/useNotificationPreferences'
import { showsToast, visibleUnreadCount } from '@/lib/utils/notificationPreferences'
import type { Notification } from '@/services/notification-service'
import { NotificationCenter } from './NotificationCenter'
import { NotificationToasts } from './notifications/NotificationToasts'
import { useOpenNotification } from './notifications/useOpenNotification'
import { ACCENT, ACCENT_FILL } from './notifications/notificationStyles'
import type { PanelAnchor } from './notifications/panelSize'
import { Button } from '../ui/button'
import { Tooltip } from '../ui/tooltip'
import { Bell, BellRing } from 'lucide-react'

interface NotificationBellProps {
  className?: string
  /**
   * Where the bell lives: in the desktop taskbar (default; panel and toasts
   * bottom right) or in the icon rail of the shell-free routes (#1169; panel
   * and toasts bottom left beside the rail, the bell styled as a rail entry).
   */
  anchor?: PanelAnchor
}

/** The unread number on the bell; capped so it fits the badge. */
function UnreadBadge({ count, className }: { count: number; className: string }) {
  return (
    <span
      aria-hidden
      className={`absolute z-10 h-[18px] min-w-[18px] px-[5px] rounded-full flex items-center justify-center text-[11px] font-semibold ${className}`}
      style={ACCENT_FILL}
    >
      {count > 99 ? '99+' : count}
    </span>
  )
}

/** Fired when the center opens, so the widget bar can step aside (#1822). */
export const NOTIFICATION_CENTER_OPENED = 'notification-center-opened'

export const NotificationBell: React.FC<NotificationBellProps> = ({ className = '', anchor = 'taskbar-right' }) => {
  const { t } = useTranslation()
  const [isNotificationCenterOpen, setIsNotificationCenterOpen] = useState(false)
  const bellRef = useRef<HTMLButtonElement>(null)
  const notificationsHook = useNotificationsContext()
  const { notifications, isLoading, markAsRead } = notificationsHook
  const { preferences } = useNotificationPreferences()
  const unreadCount = visibleUnreadCount(notificationsHook.unreadCount, notifications, preferences)
  const toastAllowed = useCallback((n: Notification) => showsToast(preferences, n), [preferences])

  // Toasts live with the bell: it is mounted once per desktop (taskbar) or once
  // per standalone shell (rail).
  // They hold back while any panel is open — also one opened from the widget area.
  const panel = useNotificationPanel()
  const { toasts, dismiss } = useNotificationToasts({
    notifications,
    isLoading,
    suppressed: panel?.isPanelOpen ?? isNotificationCenterOpen,
    allowed: toastAllowed,
  })
  const openNotification = useOpenNotification(notifications, markAsRead)
  const handleToastOpen = useCallback(
    (notification: Notification) => {
      dismiss(notification.id)
      // Without a target the toast leads to the panel, where the row is.
      if (!openNotification(notification)) setIsNotificationCenterOpen(true)
    },
    [dismiss, openNotification],
  )

  const label =
    unreadCount > 0
      ? t('notificationCenter.bellLabelUnread', { count: unreadCount })
      : t('notificationCenter.title')

  const toggle = () => setIsNotificationCenterOpen((open) => !open)

  useEffect(() => {
    if (isNotificationCenterOpen) window.dispatchEvent(new CustomEvent(NOTIFICATION_CENTER_OPENED))
  }, [isNotificationCenterOpen])
  const icon =
    unreadCount > 0 ? (
      <BellRing className="h-5 w-5" style={{ color: ACCENT }} aria-hidden />
    ) : (
      <Bell className={`h-5 w-5 ${anchor === 'rail-left' ? '' : 'text-[var(--text-color)]'}`} aria-hidden />
    )

  const trigger =
    anchor === 'rail-left' ? (
      // Same look as the other rail entries (`StandaloneLayout`), with the rail's tooltip.
      <Tooltip content={label} side="right" delayDuration={300}>
        <button
          ref={bellRef}
          type="button"
          onClick={toggle}
          data-testid="standalone-rail-bell"
          aria-label={label}
          aria-haspopup="dialog"
          aria-expanded={isNotificationCenterOpen}
          className={[
            'relative grid h-11 w-11 place-items-center rounded-xl transition-colors',
            'text-[var(--text-secondary)] hover:bg-[var(--sidebar-hover)] hover:text-[var(--text-color)]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            isNotificationCenterOpen ? 'bg-[var(--sidebar-hover)] text-[var(--text-color)]' : '',
            className,
          ].join(' ')}
        >
          {icon}
          {unreadCount > 0 && <UnreadBadge count={unreadCount} className="top-0.5 right-0.5" />}
        </button>
      </Tooltip>
    ) : (
      <div className={`relative ${className}`}>
        <Button
          ref={bellRef}
          variant="ghost"
          size="sm"
          onClick={toggle}
          data-testid="notification-bell"
          className="relative p-2 hover:bg-[var(--menu-item-hover)] transition-colors"
          aria-label={label}
          title={label}
          aria-haspopup="dialog"
          aria-expanded={isNotificationCenterOpen}
        >
          {icon}
          {unreadCount > 0 && <UnreadBadge count={unreadCount} className="-top-1 -right-1" />}
        </Button>
      </div>
    )

  return (
    <>
      {trigger}

      <NotificationCenter
        isOpen={isNotificationCenterOpen}
        onClose={() => setIsNotificationCenterOpen(false)}
        controller={notificationsHook}
        anchorRef={bellRef}
        anchor={anchor}
      />

      <NotificationToasts toasts={toasts} onOpen={handleToastOpen} onDismiss={dismiss} anchor={anchor} />
    </>
  )
}
